export interface Coordinates {
  latitude: number;
  longitude: number;
}

interface AddressFields {
  indirizzo?: string | null;
  citta?: string | null;
  provincia?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
}

export type CoordinatesResolution = 'present' | 'resolved' | 'not-found';

const isBlank = (value: unknown) =>
  value === null || value === undefined || String(value).trim() === '';

/**
 * Coordinate dell'indirizzo via Nominatim (OpenStreetMap), arrotondate a 6
 * decimali. null se l'indirizzo non viene trovato; gli errori di rete vengono
 * propagati al chiamante.
 */
export const geocodeAddress = async ({
  indirizzo,
  citta,
  provincia,
}: AddressFields): Promise<Coordinates | null> => {
  if (isBlank(indirizzo) || isBlank(citta)) return null;

  const query = `${indirizzo}, ${citta}${provincia ? `, ${provincia}` : ''}`;
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
    query,
  )}&format=json&countrycodes=it&limit=5`;

  const response = await fetch(url);
  const data = await response.json();
  if (!Array.isArray(data) || data.length === 0) return null;

  const result =
    data.find((loc: any) => loc.addresstype !== 'county') || data[0];
  const latitude = Number(Number(result.lat).toFixed(6));
  const longitude = Number(Number(result.lon).toFixed(6));
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

  return { latitude, longitude };
};

/**
 * Al salvataggio: se l'esercizio ha l'indirizzo ma non le coordinate, le
 * ricava dall'indirizzo. Senza coordinate l'esercizio non compare sulla mappa
 * di onav.it. Se il geocoding non trova l'indirizzo (o fallisce) i dati
 * restano invariati: il salvataggio non va bloccato.
 */
export const withResolvedCoordinates = async <T extends AddressFields>(
  data: T,
): Promise<{ data: T; coordinates: CoordinatesResolution }> => {
  if (!isBlank(data.latitude) && !isBlank(data.longitude)) {
    return { data, coordinates: 'present' };
  }

  try {
    const coordinates = await geocodeAddress(data);
    if (coordinates) {
      return { data: { ...data, ...coordinates }, coordinates: 'resolved' };
    }
  } catch (error) {
    console.error('Errore geocoding:', error);
  }

  return { data, coordinates: 'not-found' };
};

export const COORDINATES_NOT_FOUND_WARNING =
  "Coordinate non trovate per l'indirizzo: l'esercizio non comparirà sulla mappa di onav.it finché non vengono inserite.";
